import React, { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useStep1ViewModel } from "@/features/properties/viewmodel/useStep1ViewModel";
import { AddressSearch, type AddressSuggestion } from "@/shared/components/common/AddressSearch";
import { GoogleMap } from "@/shared/components/common/GoogleMap";
import styles from "./HomePage.module.css";
import {
  useHomePageViewModal,
  type CommunityMapMarker,
  type RecordItem,
} from "@/features/home/viewModal/homePageViewModal";
import { ServiceExpansionRequestModal } from "@/features/home/view/ServiceExpansionRequestModal";
import { CommunityRecordDetailModal } from "@/features/home/view/CommunityRecordDetailModal";
import { ProtestDetailsModal } from "@/features/home/view/ProtestDetailsModal";
import { ExemptionDetailsModal } from "@/features/home/view/ExemptionDetailsModal";
import { Modal } from "@/shared/modal";
import { useAddress } from "@/shared/hooks";
import { useAddressListViewModel } from "@/shared/components/common/AddressSelectPill/AdrressListviewmodel";
import { powerbillService } from "@/features/powerBill/services/powerBill.services";
import {
  propertyService,
  propertySupportService,
} from "@/features/properties/model/propertyServices";
import type { PropertyApiResponse } from "@/features/properties/model/propertiesTypes";
import { routes } from "@/app/navigation/routes";
import { useAuth } from "@/app/providers/useAuth";
import { tokenService } from "@/services/auth/token.service";
import { toastService } from "@/shared/toast";
import andyHdLogo from "@/assets/andyhdlogo.png";
import houseVector from "@/assets/images/banner background (2).png";
import guestPaidHouseImg from "@/assets/images/settings-house.png";
import { usePropertyStreetViews, type PropertyLike } from "@/shared/utils";
import { Images } from "@/assets/images1";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { removeGuestProperty } from "@/features/properties/model/guestSession";

const mapImgPlaceholder =
  "https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=800";

const LockIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2.5" ry="2.5" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    <circle cx="12" cy="16" r="1.2" fill="currentColor" />
    <path d="M12 17.2v1.8" />
  </svg>
);

const parsePropertyAddress = (rawLabel: string) => {
  if (!rawLabel) return { line1: "Property Address", line2: "TX" };

  const parts = rawLabel
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length === 0) return { line1: rawLabel, line2: "" };
  if (parts.length === 1) return { line1: parts[0], line2: "TX" };

  const line1 = parts[0];
  const remaining = parts.slice(1);

  // Deduplicate repeating parts (e.g. city, state, zip duplicated in raw address)
  const uniqueParts: string[] = [];
  const seen = new Set<string>();
  for (const part of remaining) {
    const key = part.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      uniqueParts.push(part);
    }
  }

  let line2 = uniqueParts.join(", ");
  // Format standard US State & Zip nicely (e.g. "CA, 91780" -> "CA 91780")
  line2 = line2.replace(/,\s*([A-Za-z]{2}),\s*(\d{5}(-\d{4})?)/g, ", $1 $2");

  return { line1, line2: line2 || "TX" };
};

interface ProtestStatusInfo {
  key: string;
  badgeLabel: string;
  fullLabel: string;
  badgeClass: string;
}

const getProtestStatusInfo = (rawStatus?: string | null): ProtestStatusInfo => {
  const normalized = (rawStatus || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");

  if (normalized === "filed") {
    return {
      key: "filed",
      badgeLabel: "Filed",
      fullLabel: "Protest: Filed",
      badgeClass: styles.statusBadgeFiled ?? "",
    };
  }
  if (normalized === "inprogress" || normalized === "in_progress") {
    return {
      key: "inprogress",
      badgeLabel: "In Progress",
      fullLabel: "Protest: In Progress",
      badgeClass: styles.statusBadgeInProgress ?? "",
    };
  }
  if (normalized === "approved") {
    return {
      key: "approved",
      badgeLabel: "Approved",
      fullLabel: "Protest: Approved",
      badgeClass: styles.statusBadgeApproved ?? "",
    };
  }
  if (normalized === "reject" || normalized === "rejected") {
    return {
      key: "rejected",
      badgeLabel: "Rejected",
      fullLabel: "Protest: Rejected",
      badgeClass: styles.statusBadgeRejected ?? "",
    };
  }

  return {
    key: "not_filed",
    badgeLabel: "Not Filed",
    fullLabel: "Protest: Not Filed",
    badgeClass: styles.statusBadgeNotFiled ?? "",
  };
};

const MapSection: React.FC<{
  address: string;
  center: { lat: number; lng: number };
  markers: CommunityMapMarker[];
}> = ({ address, center, markers }) => {
  return <GoogleMap address={address} center={center} zoom={12} markers={markers} />;
};

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const vm = useHomePageViewModal();
  console.log("vm.journeyItems", vm.journeyItems);
  const { selectedAddress, setSelectedAddress } = useAddress();
  const {
    options: addressOptions,
    properties: userProperties,
    refetch: refetchOptions,
  } = useAddressListViewModel();

  const hasProperties = (addressOptions?.length ?? 0) > 0;

  const propertyMap = useMemo(() => {
    const map = new Map<string, (typeof userProperties)[number]>();
    (userProperties || []).forEach((p) => {
      if (p.property_id) map.set(p.property_id, p);
      if (p.address?.full_address) map.set(p.address.full_address, p);
      if (p.address?.street) map.set(p.address.street, p);
    });
    return map;
  }, [userProperties]);

  const selectedStatusRaw =
    vm.propertyDetails?.protest_status ||
    (selectedAddress ? propertyMap.get(selectedAddress)?.protest_status : undefined);
  const selectedStatusInfo = useMemo(
    () => getProtestStatusInfo(selectedStatusRaw),
    [selectedStatusRaw]
  );

  const queryClient = useQueryClient();
  const [deletingPropertyId, setDeletingPropertyId] = useState<string | null>(null);

  const handleDeleteProperty = useCallback(
    async (propertyId: string) => {
      if (!propertyId) return;

      try {
        setDeletingPropertyId(propertyId);

        // 1. If currently selected property is the one being deleted, switch immediately
        // so that no active queries or background effects try to fetch details for the deleted property.
        if (selectedAddress === propertyId) {
          const remaining = addressOptions.filter(
            (opt) => opt.value !== propertyId && opt.label !== propertyId
          );
          if (remaining.length > 0 && remaining[0]?.value) {
            setSelectedAddress(remaining[0].value);
          } else {
            setSelectedAddress("");
          }
        }

        // 2. Immediately cancel and remove any in-flight or cached details query for this property
        await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.PROPERTY_DETAILS, propertyId] });
        queryClient.removeQueries({ queryKey: [QUERY_KEYS.PROPERTY_DETAILS, propertyId] });

        // 3. Optimistically remove the deleted property from the properties list cache
        queryClient.setQueriesData<PropertyApiResponse[]>(
          { queryKey: [QUERY_KEYS.PROPERTIES] },
          (old) => (Array.isArray(old) ? old.filter((p) => p.property_id !== propertyId) : [])
        );

        // 4. Call delete API
        try {
          await propertyService.delete({ property_id: propertyId });
        } catch (apiErr) {
          console.warn("Delete property API error:", apiErr);
        }

        await removeGuestProperty(propertyId);
        toastService.show("Property deleted successfully.", "success");

        // 5. Invalidate and refetch properties list
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROPERTIES] });
        refetchOptions();
      } catch (err) {
        console.error("Failed to delete property:", err);
        toastService.show("Failed to delete property. Please try again.", "error");
      } finally {
        setDeletingPropertyId(null);
      }
    },
    [addressOptions, queryClient, refetchOptions, selectedAddress, setSelectedAddress]
  );

  const selectedPropItem = selectedAddress ? propertyMap.get(selectedAddress) : undefined;
  const isSelectedDeletable = selectedPropItem ? selectedPropItem.payment_status === null : false;

  const propertyItems = useMemo((): PropertyLike[] => {
    if (userProperties && userProperties.length > 0) {
      return userProperties.map((p) => ({
        property_id: p.property_id,
        address: p.address,
      }));
    }
    return addressOptions.map((opt) => ({
      property_id: opt.value,
      address: opt.label,
    }));
  }, [userProperties, addressOptions]);

  const streetViewImages = usePropertyStreetViews(propertyItems);
  const selectedPropertyImg = selectedAddress ? streetViewImages[selectedAddress] : null;

  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    if (scrollWidth <= clientWidth + 4) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    setCanScrollLeft(scrollLeft > 6);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 6);
  }, []);

  const handleScroll = (direction: "left" | "right") => {
    const el = carouselRef.current;
    if (!el) return;
    const scrollAmount = Math.max(286, Math.floor(el.clientWidth * 0.6));
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    updateScrollButtons();
    const timer = setTimeout(updateScrollButtons, 150);

    el.addEventListener("scroll", updateScrollButtons, { passive: true });
    window.addEventListener("resize", updateScrollButtons);

    return () => {
      clearTimeout(timer);
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [addressOptions, updateScrollButtons]);

  useEffect(() => {
    if (!carouselRef.current || !selectedAddress) return;
    const activeEl = carouselRef.current.querySelector<HTMLElement>(`[data-selected="true"]`);
    if (activeEl) {
      activeEl.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [selectedAddress]);

  const {
    isAddPropertyModalOpen,
    setIsAddPropertyModalOpen,
    isSubmitting,
    handleAddPropertySubmit,
  } = useStep1ViewModel();

  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [selectedEnrollPropertyIds, setSelectedEnrollPropertyIds] = useState<string[]>([]);

  const eligibleUnpaidProperties = useMemo(() => {
    return (userProperties || []).filter((p) => {
      const status = p.payment_status;
      return status === null || status === undefined || status === "unpaid";
    });
  }, [userProperties]);

  useEffect(() => {
    if (isEnrollModalOpen && eligibleUnpaidProperties.length > 0) {
      setSelectedEnrollPropertyIds(eligibleUnpaidProperties.map((p) => p.property_id));
    }
  }, [isEnrollModalOpen, eligibleUnpaidProperties]);

  const isAllEnrollSelected =
    eligibleUnpaidProperties.length > 0 &&
    selectedEnrollPropertyIds.length === eligibleUnpaidProperties.length;

  const handleToggleSelectAllEnroll = () => {
    if (isAllEnrollSelected) {
      setSelectedEnrollPropertyIds([]);
    } else {
      setSelectedEnrollPropertyIds(eligibleUnpaidProperties.map((p) => p.property_id));
    }
  };

  const handleToggleEnrollProperty = (propertyId: string) => {
    setSelectedEnrollPropertyIds((prev) =>
      prev.includes(propertyId) ? prev.filter((id) => id !== propertyId) : [...prev, propertyId]
    );
  };

  const calculatePropertySavings = useCallback((prop: PropertyApiResponse): number => {
    const savings = prop?.amount_saved;
    if (
      savings !== null &&
      savings !== undefined &&
      Number.isFinite(Number(savings)) &&
      Number(savings) > 0
    ) {
      return Number(savings);
    }
    const taxAmount = prop?.tax_info?.tax_amount;
    if (taxAmount && Number(taxAmount) > 0) {
      return Math.round(Number(taxAmount) * 0.08);
    }
    const assessedVal = prop?.tax_info?.assessed_value ?? prop?.assessed_value ?? 0;
    const marketVal = prop?.tax_info?.market_value ?? prop?.estimated_value ?? 0;
    if (assessedVal > marketVal && marketVal > 0) {
      return Math.round((assessedVal - marketVal) * 0.025);
    }
    if (marketVal > 0) {
      return Math.round(marketVal * 0.002);
    }
    return 0;
  }, []);

  const totalSelectedSavings = useMemo(() => {
    return eligibleUnpaidProperties
      .filter((p) => selectedEnrollPropertyIds.includes(p.property_id))
      .reduce((sum, p) => sum + calculatePropertySavings(p), 0);
  }, [eligibleUnpaidProperties, selectedEnrollPropertyIds, calculatePropertySavings]);

  const handleBulkPayAndFile = () => {
    if (selectedEnrollPropertyIds.length === 0) return;
    const firstId = selectedEnrollPropertyIds[0];
    if (!firstId) return;
    const firstProp = eligibleUnpaidProperties.find((p) => p.property_id === firstId);
    setSelectedAddress(firstId);
    setIsEnrollModalOpen(false);
    navigate(routes.protestPayment, {
      state: {
        selectedProperty: firstId,
        propertyDetails: firstProp,
        selectedProperties: selectedEnrollPropertyIds.map((id) => ({
          property_id: id,
          selected: true,
        })),
      },
    });
  };

  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedFilePropertyIds, setSelectedFilePropertyIds] = useState<string[]>([]);
  const [selectedSafetyRecord, setSelectedSafetyRecord] = useState<RecordItem | null>(null);
  console.log(selectedFilePropertyIds, "selectedFilePropertyIds");

  const eligiblePaidProperties = useMemo(() => {
    return (userProperties || []).filter((p) => {
      const isPaid = p.payment_status === "paid";
      const s = (p.protest_status || "")
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, "_");
      const isNotFiled = s !== "filed" && s !== "inprogress" && s !== "approved";
      return isPaid && isNotFiled;
    });
  }, [userProperties]);

  useEffect(() => {
    if (isFileModalOpen && eligiblePaidProperties.length > 0) {
      setSelectedFilePropertyIds(eligiblePaidProperties.map((p) => p.property_id));
    }
  }, [isFileModalOpen, eligiblePaidProperties]);

  const isAllFileSelected =
    eligiblePaidProperties.length > 0 &&
    selectedFilePropertyIds.length === eligiblePaidProperties.length;

  const handleToggleSelectAllFile = () => {
    if (isAllFileSelected) {
      setSelectedFilePropertyIds([]);
    } else {
      setSelectedFilePropertyIds(eligiblePaidProperties.map((p) => p.property_id));
    }
  };

  const handleToggleFileProperty = (propertyId: string) => {
    setSelectedFilePropertyIds((prev) =>
      prev.includes(propertyId) ? prev.filter((id) => id !== propertyId) : [...prev, propertyId]
    );
  };

  const totalFileSelectedSavings = useMemo(() => {
    return eligiblePaidProperties
      .filter((p) => selectedFilePropertyIds.includes(p.property_id))
      .reduce((sum, p) => sum + calculatePropertySavings(p), 0);
  }, [eligiblePaidProperties, selectedFilePropertyIds, calculatePropertySavings]);

  const handleContinueToFileAppeal = () => {
    if (eligiblePaidProperties.length === 1) {
      const singleProp = eligiblePaidProperties[0];
      if (singleProp) {
        setSelectedAddress(singleProp.property_id);
        navigate(routes.selectPropertyType, {
          state: {
            paidPropertyIds: [singleProp.property_id],
          },
        });
        return;
      }
    }
    setIsFileModalOpen(true);
  };

  const handleBulkSignAndFile = () => {
    if (selectedFilePropertyIds.length === 0) return;
    const firstId = selectedFilePropertyIds[0];
    if (!firstId) return;
    setSelectedAddress(firstId);
    setIsFileModalOpen(false);
    navigate(routes.selectPropertyType, {
      state: {
        paidPropertyIds: selectedFilePropertyIds,
      },
    });
  };

  const handleShareProperty = useCallback(async (propertyAddress: string) => {
    const shareData = {
      title: "Andy - Property Protest",
      text: `View tax protest details for ${propertyAddress}`,
      url: window.location.href,
    };

    if (
      navigator.share &&
      typeof navigator.canShare === "function" &&
      navigator.canShare(shareData)
    ) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if ((err as Error)?.name !== "AbortError") {
          console.warn("Share failed:", err);
        }
        return;
      }
    }

    try {
      await navigator.clipboard.writeText(window.location.href);
      toastService.show("Property link copied to clipboard!", "success");
    } catch {
      toastService.show("Could not copy link", "error");
    }
  }, []);

  const searchAddresses = useCallback(async (query: string): Promise<AddressSuggestion[]> => {
    if (!query || query.trim().length < 3) return [];
    try {
      const results = await propertySupportService.getRealStateAutoCompletePropertyDetails({
        search: query.trim(),
        search_types: ["A"],
      });
      if (!results || !Array.isArray(results)) return [];
      return results
        .map((item): AddressSuggestion | null => {
          const addressParts: string[] = [];
          if (item.house) addressParts.push(item.house.trim());
          if (item.street) addressParts.push(item.street.trim());
          const streetAddress = addressParts.join(" ").trim();
          let fullAddress = item.address || "";
          if (!fullAddress && streetAddress) {
            const cityStateZip = [item.city || "", item.state || "", item.zip || ""]
              .filter(Boolean)
              .join(", ");
            fullAddress = cityStateZip ? `${streetAddress}, ${cityStateZip}` : streetAddress;
          }
          if (!fullAddress && !item.id) return null;
          return {
            id: item.id || `addr_${Date.now()}_${Math.random()}`,
            address: fullAddress || "Address unavailable",
            city: item.city,
            state: item.state,
            zipCode: item.zip,
            apn: item.apn,
          };
        })
        .filter((item): item is AddressSuggestion => item !== null);
    } catch (e) {
      console.error("Error searching address in guest mode:", e);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!addressOptions || addressOptions.length === 0) {
      if (selectedAddress) {
        setSelectedAddress("");
      }
      return;
    }
    // Keep user-picked property unless current selection is missing from available options.
    if (
      selectedAddress &&
      addressOptions.some(
        (option) => option.value === selectedAddress || option.label === selectedAddress
      )
    ) {
      return;
    }
    const first = addressOptions.at(0);
    if (!first) {
      setSelectedAddress("");
      return;
    }
    setSelectedAddress(first.value);
  }, [addressOptions, selectedAddress, setSelectedAddress]);

  const selectedZip = vm.propertyDetails?.address?.zip || selectedPropItem?.address?.zip;
  const selectedState = vm.propertyDetails?.address?.state || selectedPropItem?.address?.state;
  const { setIsPowerbillEligible } = vm;

  useEffect(() => {
    if (!selectedZip || !selectedState) {
      setIsPowerbillEligible(false);
      return;
    }
    let isCancelled = false;

    powerbillService
      .getEletricityEligible(selectedZip, selectedState, "")
      .then((response) => {
        if (!isCancelled) {
          setIsPowerbillEligible(Boolean(response?.data?.is_eligible));
        }
      })
      .catch((err) => {
        console.warn("Failed to check electricity eligibility:", err);
        if (!isCancelled) {
          setIsPowerbillEligible(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedZip, selectedState, setIsPowerbillEligible]);

  // Derive step states from vm data
  const isPaid = Boolean(
    vm.isPaymentCompleted ||
    (selectedAddress && propertyMap.get(selectedAddress)?.payment_status === "paid")
  );
  const isStep1Complete = true; // Property added is always true if we're here
  const isStep2Complete = isPaid;
  const isStep3Complete = vm.hasProtestFiled;
  const isStep4Complete = vm.isProtestApproved || vm.isProtestRejected;

  const currentStep = !isStep2Complete ? 2 : !isStep3Complete ? 3 : !isStep4Complete ? 4 : 5;

  let completedSteps = 1;
  if (isStep4Complete) completedSteps = 4;
  else if (isStep3Complete) completedSteps = 3;
  else if (isStep2Complete) completedSteps = 2;

  // Guest Paid Protest Banner Logic:
  // When a guest user (not logged in / signed up) has a property with a completed protest payment,
  // show the card prompting them to create an account and finish filing with the county.
  const isGuestUser = !isAuthenticated && !tokenService.hasToken();

  const guestPaidProperty = useMemo(() => {
    if (!isGuestUser) return null;

    // Must be the currently selected property
    const currentProp =
      (selectedAddress ? propertyMap.get(selectedAddress) : undefined) ||
      vm.propertyDetails ||
      userProperties?.[0];

    if (!currentProp) return null;

    const checkIsPropPaid = (prop?: PropertyApiResponse | null): boolean => {
      if (!prop) return false;
      const status = (prop.payment_status ?? "").toLowerCase();
      if (status === "paid") return true;
      if (prop.protestPayments && typeof prop.protestPayments === "object") {
        return Object.values(prop.protestPayments).some(
          (p) => Boolean(p?.paid) || (p?.status ?? "").toLowerCase() === "paid"
        );
      }
      return false;
    };

    const isCurrentPropPaid =
      checkIsPropPaid(currentProp) ||
      (vm.propertyDetails?.property_id === currentProp.property_id &&
        checkIsPropPaid(vm.propertyDetails));

    // If unpaid, do not show
    if (!isCurrentPropPaid) {
      return null;
    }

    return currentProp;
  }, [isGuestUser, selectedAddress, propertyMap, vm.propertyDetails, userProperties]);

  const guestPaidEmail = useMemo(() => {
    if (!guestPaidProperty) return "";

    if (guestPaidProperty.protestPayments) {
      for (const pay of Object.values(guestPaidProperty.protestPayments)) {
        if (pay?.email && typeof pay.email === "string" && pay.email.trim()) {
          return pay.email.trim();
        }
      }
    }

    const propAny = guestPaidProperty as unknown as Record<string, unknown>;
    if (typeof propAny.email === "string" && propAny.email.trim()) {
      return propAny.email.trim();
    }
    if (typeof propAny.customer_email === "string" && propAny.customer_email.trim()) {
      return propAny.customer_email.trim();
    }

    if (guestPaidProperty.owner) {
      const ownerAny = guestPaidProperty.owner as unknown as Record<string, unknown>;
      if (typeof ownerAny.email === "string" && ownerAny.email.trim()) {
        return ownerAny.email.trim();
      }
    }

    return "";
  }, [guestPaidProperty]);

  const guestPaidYear = useMemo(() => {
    if (!guestPaidProperty?.protestPayments) return "2027";
    const paidYears = Object.entries(guestPaidProperty.protestPayments)
      .filter(([_, p]) => p?.paid || p?.status === "paid")
      .map(([yr]) => yr);
    if (paidYears.length > 0) {
      paidYears.sort();
      return paidYears[paidYears.length - 1];
    }
    const allYears = Object.keys(guestPaidProperty.protestPayments);
    if (allYears.length > 0) {
      allYears.sort();
      return allYears[allYears.length - 1];
    }
    return "2027";
  }, [guestPaidProperty]);

  const guestPaidAddress = useMemo(() => {
    if (!guestPaidProperty) return "";
    const addr = guestPaidProperty.address;
    let rawAddress = "";

    if (addr?.full_address && addr.full_address.trim()) {
      rawAddress = addr.full_address.trim();
    } else if (addr) {
      let line1 = addr.line1 || addr.street || "";
      if (line1.startsWith("& ")) {
        line1 = line1.substring(2).trim();
      }
      const city = addr.city || "";
      const state = addr.state || "TX";
      const zip = addr.zip || "";
      const parts: string[] = [];
      if (line1) parts.push(line1);
      const cityStateZip = [city, [state, zip].filter(Boolean).join(" ")]
        .filter(Boolean)
        .join(", ");
      if (cityStateZip) parts.push(cityStateZip);
      rawAddress = parts.join(", ");
    } else if (guestPaidProperty.name && guestPaidProperty.name.trim()) {
      rawAddress = guestPaidProperty.name.trim();
    }

    if (!rawAddress) return "Property Address";

    // Deduplicate repeating components (e.g., duplicated city/state/zip in raw full_address)
    const { line1, line2 } = parsePropertyAddress(rawAddress);
    let cleanLine1 = line1 || "";
    if (cleanLine1.startsWith("& ")) {
      cleanLine1 = cleanLine1.substring(2).trim();
    }
    return line2 ? `${cleanLine1}, ${line2}` : cleanLine1 || "Property Address";
  }, [guestPaidProperty]);

  const handleCreateAccountFromPaidGuest = useCallback(() => {
    if (!guestPaidProperty) return;
    navigate(routes.paymentSuccess, {
      state: {
        autoOpenRegister: true,
        email: guestPaidEmail,
        property: guestPaidProperty,
        propertyId: guestPaidProperty.property_id,
        year: guestPaidYear,
      },
    });
  }, [guestPaidProperty, guestPaidEmail, guestPaidYear, navigate]);

  const handleCheckEnergySavings = useCallback(
    (e: React.MouseEvent) => {
      const isUserAuthenticated = Boolean(isAuthenticated || tokenService.hasToken());
      if (isUserAuthenticated) {
        return;
      }

      e.preventDefault();
      if (guestPaidProperty) {
        handleCreateAccountFromPaidGuest();
        return;
      }

      const currentProp =
        (selectedAddress ? propertyMap.get(selectedAddress) : undefined) ||
        vm.propertyDetails ||
        userProperties?.[0];
      const currentPropId = currentProp?.property_id || selectedAddress;

      if (currentProp || currentPropId) {
        navigate(routes.paymentSuccess, {
          state: {
            autoOpenRegister: true,
            email: guestPaidEmail || "",
            property: currentProp,
            propertyId: currentPropId,
            year: guestPaidYear || "2027",
          },
        });
      } else {
        navigate(routes.login);
      }
    },
    [
      isAuthenticated,
      guestPaidProperty,
      handleCreateAccountFromPaidGuest,
      selectedAddress,
      propertyMap,
      vm.propertyDetails,
      userProperties,
      navigate,
      guestPaidEmail,
      guestPaidYear,
    ]
  );

  return (
    <div className={styles.homePage}>
      <div className={styles.layout}>
        {/* GUEST PAID PROTEST CARD */}

        {/* HERO BANNER */}
        <div className={styles.heroBanner}>
          <img src={houseVector} alt="" className={styles.heroBackground} />
          <div className={styles.heroLeft}>
            <img src={andyHdLogo} alt="Andy" className={styles.heroAndy} />
          </div>

          <div className={styles.heroRight}>
            <div className={styles.heroText}>
              <h1 className={styles.heroTitle}>My Properties</h1>
              <p className={styles.heroDesc}>
                Manage your properties, track appeals and
                <br className={styles.desktopBr} />
                save on more properties.
              </p>
            </div>
            {hasProperties && (
              <div className={styles.heroActions}>
                <button
                  className={styles.addPropertyBtn}
                  onClick={() => setIsAddPropertyModalOpen(true)}
                >
                  + Add Property
                </button>
                <button className={styles.viewAllBtn} onClick={() => navigate(routes.properties)}>
                  View All ➔
                </button>
              </div>
            )}
          </div>
        </div>
        {guestPaidProperty && (
          <div className={styles.guestPaidCard}>
            {/* Top-Right Decorative Curve Backdrop */}
            <svg
              className={styles.guestPaidCornerShape}
              viewBox="0 0 310 160"
              preserveAspectRatio="none"
              fill="none"
              aria-hidden="true"
            >
              <path d="M85 0 C42 45 15 105 0 160 L310 160 L310 0 Z" fill="#ddf1e6" />
            </svg>

            {/* Sparkle Stars in Top Right */}
            <div className={styles.guestPaidSparkles} aria-hidden="true">
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                className={styles.guestPaidSparkleSmall}
              >
                <path
                  d="M12 0C12 6.627 6.627 12 0 12C6.627 12 12 17.373 12 24C12 17.373 17.373 12 24 12C17.373 12 12 6.627 12 0Z"
                  fill="#eab308"
                />
              </svg>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                className={styles.guestPaidSparkleLarge}
              >
                <path
                  d="M12 0C12 6.627 6.627 12 0 12C6.627 12 12 17.373 12 24C12 17.373 17.373 12 24 12C17.373 12 12 6.627 12 0Z"
                  fill="#f59e0b"
                />
              </svg>
            </div>

            {/* House Background Illustration */}
            <img
              src={guestPaidHouseImg}
              alt=""
              className={styles.guestPaidHouseBg}
              aria-hidden="true"
            />

            <div className={styles.guestPaidLeft}>
              <div className={styles.guestPaidHeader}>
                <div className={styles.guestPaidCheckBadgeWrapper} aria-hidden="true">
                  <svg
                    className={styles.guestPaidRays}
                    width="26"
                    height="11"
                    viewBox="0 0 26 11"
                    fill="none"
                  >
                    <line
                      x1="4"
                      y1="10"
                      x2="1.5"
                      y2="3.5"
                      stroke="#086338"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                    />
                    <line
                      x1="13"
                      y1="9"
                      x2="13"
                      y2="1.5"
                      stroke="#086338"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                    />
                    <line
                      x1="22"
                      y1="10"
                      x2="24.5"
                      y2="3.5"
                      stroke="#086338"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className={styles.guestPaidCheckBadgeOuter}>
                    <div className={styles.guestPaidCheckBadgeInner}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#ffffff"
                        strokeWidth="3.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className={styles.guestPaidTitleWrap}>
                  <h2 className={styles.guestPaidTitle}>
                    Your {guestPaidYear} protest is paid for
                  </h2>
                  {guestPaidEmail && (
                    <span className={styles.guestPaidEmail}>{guestPaidEmail}</span>
                  )}
                </div>
              </div>

              <div className={styles.guestPaidAddressRow}>
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={styles.guestPaidHouseIcon}
                  aria-hidden="true"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                <span className={styles.guestPaidAddressText}>{guestPaidAddress}</span>
              </div>

              <p className={styles.guestPaidDesc}>
                This payment was taken against the email above. Create your account to finish filing
                with the county and keep the properties you&apos;ve added.
              </p>
            </div>

            <button
              type="button"
              className={styles.guestPaidCreateBtn}
              onClick={handleCreateAccountFromPaidGuest}
            >
              <span>Create your account</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}

        <div className={styles.carouselSection}>
          <button
            type="button"
            className={styles.navArrow}
            onClick={() => handleScroll("left")}
            disabled={!canScrollLeft}
            aria-label="Previous property"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div ref={carouselRef} className={styles.carouselCards}>
            {addressOptions.length === 0 ? (
              <div
                className={styles.emptyPropertyCard}
                onClick={() => setIsAddPropertyModalOpen(true)}
                role="button"
                tabIndex={0}
              >
                <div className={styles.emptyPropertyIcon}>+</div>
                <div>
                  <h3 className={styles.propertyAddress}>No Properties Found</h3>
                  <p className={styles.propertyLocation}>Click to add your first property</p>
                </div>
              </div>
            ) : (
              addressOptions.map((opt) => {
                const isSelected = selectedAddress === opt.label || selectedAddress === opt.value;
                const { line1, line2 } = parsePropertyAddress(opt.label);
                const propItem = propertyMap.get(opt.value) || propertyMap.get(opt.label);
                const rawStatus =
                  (isSelected && vm.propertyDetails?.protest_status) || propItem?.protest_status;
                const statusInfo = getProtestStatusInfo(rawStatus);

                return (
                  <div
                    key={opt.value}
                    data-selected={isSelected}
                    className={`${styles.propertyCard} ${isSelected ? styles.propertyCardActive : ""}`}
                    onClick={() => setSelectedAddress(opt.value)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedAddress(opt.value);
                      }
                    }}
                  >
                    <div className={styles.propertyImgWrapper}>
                      {isSelected && (
                        <div className={styles.currentBadge}>
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          Current
                        </div>
                      )}

                      {/* Top-Right: When filed, show circular share button matching mockup; otherwise status badge */}
                      {statusInfo.key === "filed" ? (
                        <button
                          type="button"
                          className={styles.propertyShareBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShareProperty(opt.label);
                          }}
                          aria-label="Share property"
                          title="Share property"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                        </button>
                      ) : (
                        <div
                          className={`${styles.protestStatusBadge} ${statusInfo.badgeClass}`}
                          title={`Protest status: ${statusInfo.badgeLabel}`}
                        >
                          <span className={styles.statusDot} />
                          {statusInfo.badgeLabel}
                        </div>
                      )}

                      {/* Official FILED Stamp Banner Overlay matching exact mockup */}
                      {statusInfo.key === "filed" && (
                        <div className={styles.filedBannerStamp}>
                          <div className={styles.filedBannerStampInner}>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className={styles.filedBannerCheck}
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span className={styles.filedBannerText}>FILED</span>
                          </div>
                        </div>
                      )}

                      {streetViewImages[opt.value] ? (
                        <img
                          src={streetViewImages[opt.value]!}
                          alt={opt.label}
                          className={styles.propertyImg}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const fallback = e.currentTarget.parentElement?.querySelector(
                              `.${styles.propertyImgPlaceholder}`
                            ) as HTMLElement | null;
                            if (fallback) fallback.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className={styles.propertyImgPlaceholder}
                        style={{ display: streetViewImages[opt.value] ? "none" : "flex" }}
                      >
                        <img
                          src={Images.noPropertyHome}
                          alt="Property"
                          className={styles.propertyFallbackIcon}
                        />
                      </div>
                    </div>

                    <div className={styles.propertyBody}>
                      <div className={styles.propertyHeader}>
                        <h3 className={styles.propertyAddress} title={line1}>
                          {line1}
                        </h3>
                        <p className={styles.propertyLocation} title={line2}>
                          {line2}
                        </p>
                      </div>

                      <div className={styles.propertyFooterRow}>
                        <div
                          className={`${styles.propertyStatusBadgePill} ${statusInfo.badgeClass}`}
                          title={`Protest status: ${statusInfo.badgeLabel}`}
                        >
                          <span className={styles.statusDot} />
                          <span>{statusInfo.badgeLabel}</span>
                        </div>
                        <div className={styles.propertyFooterActions}>
                          {propItem?.payment_status === null && (
                            <button
                              type="button"
                              className={styles.propertyDeleteBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDeleteProperty(opt.value);
                              }}
                              disabled={deletingPropertyId === opt.value}
                              aria-label={`Delete ${opt.label || "property"}`}
                              title="Delete Property"
                            >
                              {deletingPropertyId === opt.value ? (
                                <span className={styles.deletingSpinner} />
                              ) : (
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              )}
                            </button>
                          )}
                          <div
                            className={`${styles.cardArrow} ${isSelected ? styles.cardArrowActive : ""}`}
                            aria-label="View property details"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="5" y1="12" x2="19" y2="12" />
                              <polyline points="12 5 19 12 12 19" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <button
            type="button"
            className={styles.navArrow}
            onClick={() => handleScroll("right")}
            disabled={!canScrollRight}
            aria-label="Next property"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        {hasProperties && (
          <div className={styles.mainContent}>
            {/* LEFT COLUMN */}
            <div className={styles.leftCol}>
              {/* PROGRESS TRACKER */}
              <div className={styles.progressContainer}>
                <div className={styles.progressLine}></div>
                <div
                  className={styles.progressLineActive}
                  style={{ width: `${(completedSteps - 1) * 33}%` }}
                ></div>

                <div className={styles.step}>
                  <div
                    className={`${styles.stepIcon} ${isStep1Complete ? styles.stepIconCompleted : styles.stepIconCurrent}`}
                  >
                    {isStep1Complete ? "✓" : "1"}
                  </div>
                  <div className={`${styles.stepLabel} ${styles.stepLabelCompleted}`}>
                    Property Added
                  </div>
                </div>
                <div
                  className={styles.step}
                  onClick={!isPaid ? vm.handlePaymentNavigation : undefined}
                  style={{ cursor: !isPaid ? "pointer" : "default" }}
                  role={!isPaid ? "button" : undefined}
                  tabIndex={!isPaid ? 0 : undefined}
                  title={!isPaid ? "Make Payment" : undefined}
                >
                  <div
                    className={`${styles.stepIcon} ${isStep2Complete ? styles.stepIconCompleted : currentStep === 2 ? styles.stepIconCurrent : ""}`}
                  >
                    {isStep2Complete ? "✓" : "2"}
                  </div>
                  <div
                    className={`${styles.stepLabel} ${isStep2Complete || currentStep === 2 ? styles.stepLabelCurrent : ""}`}
                  >
                    {isStep2Complete ? "Payment Completed" : "Make Payment"}
                  </div>
                </div>
                <div className={styles.step}>
                  <div
                    className={`${styles.stepIcon} ${isStep3Complete ? styles.stepIconCompleted : currentStep === 3 ? styles.stepIconCurrent : ""}`}
                  >
                    {isStep3Complete ? "✓" : currentStep === 3 ? "3" : ""}
                  </div>
                  <div
                    className={`${styles.stepLabel} ${isStep3Complete || currentStep === 3 ? styles.stepLabelCurrent : ""}`}
                  >
                    Filed with County
                  </div>
                </div>
                <div className={styles.step}>
                  <div
                    className={`${styles.stepIcon} ${isStep4Complete ? styles.stepIconCompleted : currentStep === 4 ? styles.stepIconCurrent : ""}`}
                  >
                    {isStep4Complete ? "✓" : currentStep === 4 ? "4" : ""}
                  </div>
                  <div
                    className={`${styles.stepLabel} ${isStep4Complete || currentStep === 4 ? styles.stepLabelCurrent : ""}`}
                  >
                    Result & Savings
                  </div>
                </div>
              </div>

              {/* PROMO BANNER */}
              {eligibleUnpaidProperties.length > 0 && (
                <div className={styles.promoBanner}>
                  <div className={styles.promoLeft}>
                    <div className={styles.promoIconWrap}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                        <polyline points="2 12 12 17 22 12"></polyline>
                        <polyline points="2 17 12 22 22 17"></polyline>
                      </svg>
                    </div>
                    <div>
                      <h3 className={styles.promoTitle}>Save on More Properties</h3>
                      <p className={styles.promoDesc}>
                        {eligibleUnpaidProperties.length}{" "}
                        {eligibleUnpaidProperties.length === 1 ? "property is" : "properties are"}{" "}
                        eligible for a 2027 appeal & prior years refund. Bundle them and save.
                      </p>
                    </div>
                  </div>
                  <button className={styles.enrollBtn} onClick={() => setIsEnrollModalOpen(true)}>
                    Enroll To Save More
                  </button>
                </div>
              )}

              {/* READY TO FILE PROTEST BANNER */}
              {/* {Boolean(isAuthenticated || tokenService.hasToken()) && */}
              {eligiblePaidProperties.length > 0 && (
                <div className={styles.fileProtestBanner}>
                  <div className={styles.fileProtestLeft}>
                    <div className={styles.fileProtestIconWrap}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M12 11v4" />
                        <path d="M12 18h.01" />
                      </svg>
                    </div>
                    <div>
                      <h3 className={styles.fileProtestTitle}>Your Appeal Is Not Complete</h3>
                      <p className={styles.fileProtestDesc}>
                        Payment for{" "}
                        {`${eligiblePaidProperties.length} ${
                          eligiblePaidProperties.length === 1 ? "property" : "properties"
                        }`}{" "}
                        is complete, but your appeal has not been submitted yet. Continue to
                        complete and file your appeal.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.fileProtestBtn}
                    onClick={handleContinueToFileAppeal}
                  >
                    Continue to File Appeal →
                  </button>
                </div>
              )}

              {/* FILED WITH COUNTY SUCCESS BANNER */}
              {/* {selectedStatusInfo.key === "filed" && (
              <div className={styles.filedSuccessBanner}>
                <div className={styles.filedSuccessLeft}>
                  <div className={styles.filedSuccessIconWrap}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </div>
                  <div>
                    <h3 className={styles.filedSuccessTitle}>Appeal Filed with County</h3>
                    <p className={styles.filedSuccessDesc}>
                      Your property tax protest has been officially filed. Andy is actively tracking
                      your appeal status and potential savings.
                    </p>
                  </div>
                </div>
                <div className={styles.filedBannerStampButton}>
                  <div className={styles.filedBannerStampButtonInner}>
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={styles.filedBannerCheck}
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span className={styles.filedBannerText}>FILED</span>
                  </div>
                </div>
              </div>
            )} */}

              {/* QUICK ACTIONS */}
              {!guestPaidProperty && (
                <div>
                  <h2 className={styles.quickActionsTitle}>Quick Actions</h2>
                  <div
                    className={styles.quickActionsGrid}
                    onWheel={(e) => {
                      const el = e.currentTarget;
                      if (
                        el.scrollWidth > el.clientWidth &&
                        Math.abs(e.deltaY) > Math.abs(e.deltaX)
                      ) {
                        const canScrollLeft = el.scrollLeft > 0;
                        const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
                        if ((e.deltaY > 0 && canScrollRight) || (e.deltaY < 0 && canScrollLeft)) {
                          el.scrollLeft += e.deltaY;
                        }
                      }
                    }}
                  >
                    {vm.journeyItems.map((item) => {
                      let icon;
                      switch (item.key) {
                        case "payment":
                          icon = (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                              <line x1="1" y1="10" x2="23" y2="10"></line>
                            </svg>
                          );
                          break;
                        case "sign-documents":
                          icon = (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                              <line x1="16" y1="13" x2="8" y2="13"></line>
                              <line x1="16" y1="17" x2="8" y2="17"></line>
                              <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                          );
                          break;
                        case "supporting-documents":
                          icon = (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="17 8 12 3 7 8"></polyline>
                              <line x1="12" y1="3" x2="12" y2="15"></line>
                            </svg>
                          );
                          break;
                        case "homestead":
                          icon = (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                              <polyline points="9 22 9 12 15 12 15 22"></polyline>
                            </svg>
                          );
                          break;
                        case "protest-details":
                          icon = (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                              <path d="M9 15l2 2 4-4"></path>
                            </svg>
                          );
                          break;
                        case "exemption-history":
                          icon = (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                              <path d="M9 12l2 2 4-4"></path>
                            </svg>
                          );
                          break;
                        default:
                          icon = (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <circle cx="12" cy="12" r="10"></circle>
                              <polyline points="12 16 16 12 12 8"></polyline>
                              <line x1="8" y1="12" x2="16" y2="12"></line>
                            </svg>
                          );
                      }

                      return (
                        <div
                          key={item.key}
                          className={`${styles.actionCard} ${!item.isActive ? styles.actionCardDisabled : ""}`}
                          onClick={item.isActive ? item.onClick : undefined}
                          style={
                            !item.isActive ? { opacity: 0.5, cursor: "not-allowed" } : undefined
                          }
                        >
                          <div className={styles.actionCardLeft}>
                            <div className={styles.actionIconWrap}>{icon}</div>
                            <div>
                              <h4 className={styles.actionTitle}>{item.title}</h4>
                              <p className={styles.actionDesc}>{item.subtitle}</p>
                            </div>
                          </div>
                          <div className={styles.cardArrow}>➔</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* DETAILS CARD */}
              <div className={styles.detailsCard}>
                <div className={styles.detailsImgWrapper}>
                  {selectedPropertyImg ? (
                    <img
                      src={selectedPropertyImg}
                      alt={vm.effectiveAddress}
                      className={styles.detailsImg}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget.parentElement?.querySelector(
                          `.${styles.detailsImgPlaceholder}`
                        ) as HTMLElement | null;
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className={styles.detailsImgPlaceholder}
                    style={{ display: selectedPropertyImg ? "none" : "flex" }}
                  >
                    <img
                      src={Images.noPropertyHome}
                      alt="Property"
                      className={styles.detailsFallbackIcon}
                    />
                  </div>

                  {/* Share Button (top-right of details image) */}
                  <button
                    type="button"
                    className={styles.propertyShareBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShareProperty(vm.effectiveAddress);
                    }}
                    aria-label="Share property"
                    title="Share property"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  </button>

                  {/* Delete Button (top-right of details image, only if payment_status is null) */}
                  {isSelectedDeletable && (
                    <button
                      type="button"
                      className={styles.propertyDetailsDeleteBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedAddress) void handleDeleteProperty(selectedAddress);
                      }}
                      disabled={deletingPropertyId === selectedAddress}
                      aria-label="Delete property"
                      title="Delete Property"
                    >
                      {deletingPropertyId === selectedAddress ? (
                        <span className={styles.deletingSpinner} />
                      ) : (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      )}
                    </button>
                  )}

                  {/* Official FILED Stamp Banner Overlay on Details Card */}
                  {selectedStatusInfo.key === "filed" && (
                    <div className={styles.filedBannerStampLg}>
                      <div className={styles.filedBannerStampInnerLg}>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className={styles.filedBannerCheck}
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span className={styles.filedBannerTextLg}>FILED</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.detailsContent}>
                  <div className={styles.detailsHeader}>
                    <div className={styles.detailsIconWrap}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div className={styles.detailsTitleWrap}>
                      <h3 className={styles.detailsAddress}>
                        {vm.effectiveAddress.split(",")[0] || "509 Texas Ave"},{" "}
                        <br className={styles.desktopBr} />
                        {vm.effectiveAddress.split(",").slice(1).join(",").trim() ||
                          "Shiner, TX 77984"}
                      </h3>
                      <div
                        className={`${styles.detailsStatusPill} ${selectedStatusInfo.badgeClass}`}
                      >
                        <span className={styles.statusDot} />
                        Protest: {selectedStatusInfo.badgeLabel}
                      </div>
                    </div>
                  </div>

                  <h4 className={styles.savingsLabel}>ESTIMATED YEARLY SAVINGS</h4>
                  <div className={styles.savingsValueLg}>
                    {vm.isPropertyDetailsLoading ? "..." : vm.formatCurrency(vm.amountSaved ?? 389)}
                    <span>/YR</span>
                  </div>

                  <div className={styles.metricsRow}>
                    <div className={styles.metric}>
                      <span className={styles.metricLabel}>Market Value</span>
                      <span className={styles.metricValue}>
                        {vm.isPropertyDetailsLoading ? "..." : vm.formatCurrency(vm.marketValue)}
                      </span>
                    </div>
                    <div className={styles.metric}>
                      <span className={styles.metricLabel}>Estimated Tax Bill</span>
                      <span className={styles.metricValue}>
                        {vm.isPropertyDetailsLoading ? "..." : vm.formatCurrency(vm.totalTaxBill)}
                      </span>
                    </div>
                    <div className={styles.metric}>
                      <span className={styles.metricLabel}>Assessed Value</span>
                      <span className={styles.metricValue}>
                        {vm.isPropertyDetailsLoading ? "..." : vm.formatCurrency(vm.assessedValue)}
                      </span>
                    </div>
                  </div>

                  {/* Document & Invoice Action Buttons */}
                  {(vm.isRefundInvoicePayable ||
                    vm.showSettlementWaiverDocument ||
                    vm.showRefundInvoice) && (
                    <div className={styles.detailsActionsRow}>
                      {vm.isRefundInvoicePayable && (
                        <button
                          type="button"
                          className={styles.detailsPayNowBtn}
                          onClick={vm.handlePayRefundInvoice}
                          title="Pay Now"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <rect x="2" y="5" width="20" height="14" rx="2" />
                            <line x1="2" y1="10" x2="22" y2="10" />
                          </svg>
                          <span>Pay Now</span>
                        </button>
                      )}

                      {vm.showSettlementWaiverDocument && (
                        <button
                          type="button"
                          className={styles.detailsDocBtn}
                          onClick={vm.handleViewSettlementWaiverDocument}
                          title="View Settlement Doc"
                        >
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          <span>View Settlement Doc</span>
                        </button>
                      )}

                      {vm.showRefundInvoice && (
                        <button
                          type="button"
                          className={styles.detailsDocBtn}
                          onClick={vm.handleViewRefundInvoice}
                          title="View Invoice"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                          </svg>
                          <span>View Invoice</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className={styles.rightCol}>
              {/* ENERGY SAVINGS CARD */}
              <div className={`${styles.sidebarCard} ${styles.energyCard}`}>
                <div className={styles.energyHeader}>
                  <div className={styles.energyIcon}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11 21.883l-6.235-7.527-.765.144 5.621-12.5 6.235 7.527.765-.144-5.621 12.5z"></path>
                    </svg>
                  </div>
                  <h3 className={styles.energyTitle}>Energy Savings</h3>
                </div>
                <p className={styles.energyDesc}>
                  Better electricity rates may be available in your area. See how much you could
                  save.
                </p>
                <Link
                  to={routes.powerCardPlans}
                  state={{ selectedProperty: selectedAddress }}
                  className={styles.checkSavingsLink}
                  onClick={handleCheckEnergySavings}
                >
                  Check Savings ➔
                </Link>
                <img src={andyHdLogo} alt="" className={styles.energyAndy} />
              </div>

              {/* COMMUNITY DATA CARD */}
              <div className={styles.sidebarCard}>
                <div className={styles.communityHeader}>
                  <div className={styles.communityTitleRow}>
                    <div className={styles.communityIcon}>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    </div>
                    <h3 className={styles.communityTitle}>Community Safety</h3>
                  </div>

                  <div className={styles.communityTabs}>
                    <button
                      type="button"
                      className={`${styles.tabBtn} ${vm.activeTab === "crime" ? styles.tabBtnActive : ""}`}
                      onClick={() => vm.setActiveTab("crime")}
                    >
                      Crime Report ({vm.crimeCount})
                    </button>
                    <button
                      type="button"
                      className={`${styles.tabBtn} ${vm.activeTab === "offenders" ? styles.tabBtnActive : ""}`}
                      onClick={() => vm.setActiveTab("offenders")}
                    >
                      Sex Offenders ({vm.offendersCount})
                    </button>
                  </div>
                </div>

                <div className={styles.recordsHeader}>RECENT RECORDS</div>

                <div
                  className={styles.recordsList}
                  ref={vm.recordsListRef}
                  style={{
                    maxHeight: "300px",
                    overflowY: isPaid ? "auto" : "hidden",
                    position: "relative",
                    minHeight: "150px",
                  }}
                >
                  {!isPaid && (
                    <div
                      className={styles.lockedOverlay}
                      // onClick={() => {
                      //   navigate(routes.protestPayment, {
                      //     state: {
                      //       selectedProperty: selectedAddress,
                      //       propertyDetails:
                      //         vm.propertyDetails ||
                      //         (selectedAddress ? propertyMap.get(selectedAddress) : undefined),
                      //     },
                      //   });
                      // }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className={styles.lockIconCircle} aria-hidden="true">
                        <LockIcon size={20} />
                      </div>
                      <div className={styles.lockedSubtitle}>Complete your Payment to unlock</div>
                    </div>
                  )}

                  <div
                    style={{
                      filter: !isPaid ? "blur(4px)" : "none",
                      pointerEvents: !isPaid ? "none" : "auto",
                      userSelect: !isPaid ? "none" : "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    {vm.records && vm.records.length > 0 ? (
                      vm.records.map((record, idx) => (
                        <div
                          className={styles.recordItem}
                          key={record.id || idx}
                          onClick={() => {
                            if (isPaid) {
                              setSelectedSafetyRecord(record);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if ((e.key === "Enter" || e.key === " ") && isPaid) {
                              setSelectedSafetyRecord(record);
                            }
                          }}
                        >
                          {record.imageUrl ? (
                            <img
                              src={record.imageUrl}
                              alt="Offender"
                              className={styles.recordImg}
                            />
                          ) : (
                            <div className={styles.recordIconPlaceholder}>
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                              </svg>
                            </div>
                          )}
                          <div className={styles.recordInfo}>
                            <div className={styles.recordName}>{record.name}</div>
                            <div className={styles.recordSubtitle}>{record.subtitle}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className={styles.emptyRecords}>
                        <div className={styles.emptyRecordsIcon}>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                          </svg>
                        </div>
                        <span>No records found in this area.</span>
                      </div>
                    )}
                  </div>
                </div>

                {!isPaid ? (
                  <Link
                    to={routes.protestPayment}
                    state={{
                      selectedProperty: selectedAddress,
                      propertyDetails:
                        vm.propertyDetails ||
                        (selectedAddress ? propertyMap.get(selectedAddress) : undefined),
                    }}
                    className={styles.checkSavingsLink}
                  >
                    {/* Complete your payment to unlock ➔ */}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className={styles.checkSavingsLink}
                    onClick={() => vm.setIsMapModalOpen(true)}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      font: "inherit",
                    }}
                  >
                    View Full Details ➔
                  </button>
                )}
              </div>

              {/* MAP CARD */}
              <div
                className={styles.mapCard}
                onClick={() => {
                  if (!isPaid) {
                    navigate(routes.protestPayment, {
                      state: {
                        selectedProperty: selectedAddress,
                        propertyDetails:
                          vm.propertyDetails ||
                          (selectedAddress ? propertyMap.get(selectedAddress) : undefined),
                      },
                    });
                    return;
                  }
                  vm.setIsMapModalOpen(true);
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    filter: !isPaid ? "blur(4px)" : "none",
                    pointerEvents: !isPaid ? "none" : "auto",
                  }}
                >
                  {vm.communityMarkers && vm.communityMarkers.length > 0 ? (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                    >
                      <MapSection
                        address={vm.effectiveAddress}
                        center={vm.mapCenter}
                        markers={vm.communityMarkers}
                      />
                    </div>
                  ) : (
                    <img src={mapImgPlaceholder} alt="Map" className={styles.mapImage} />
                  )}
                </div>

                {isPaid && <button className={styles.openMapBtn}>Open Map ↗</button>}
                {/* <div className={styles.mapOverlay}>
                <h4 className={styles.mapOverlayTitle}>Community Data</h4>
                <p className={styles.mapOverlayDesc}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  {vm.effectiveAddress || "6676 Texas Highway 43, Bivins, TX 75555"}
                </p>
              </div> */}

                {!isPaid && (
                  <div className={styles.lockedOverlay}>
                    <div className={styles.lockIconCircle} aria-hidden="true">
                      <LockIcon size={20} />
                    </div>
                    <div className={styles.lockedSubtitle}>Complete your Payment to unlock</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* MODALS */}
        <ServiceExpansionRequestModal
          isOpen={vm.isServiceExpansionModalOpen}
          onClose={vm.closeServiceExpansionModal}
          defaults={vm.serviceExpansionDefaults}
        />

        <Modal
          isOpen={vm.isMapModalOpen}
          onClose={() => vm.setIsMapModalOpen(false)}
          unconstrained
          className={styles.mapModalContent}
          headerClassName={styles.mapModalHeader}
          title={
            <>
              <div className={styles.mapTitle}>Community Data</div>
              <div className={styles.mapAddress}>{vm.effectiveAddress}</div>
            </>
          }
          titleClassName={styles.mapModalTitleGroup}
          bodyClassName={styles.mapModalBody}
          ariaLabel="Community data map"
        >
          <div style={{ width: "100%", height: "65vh", minHeight: "450px" }}>
            <GoogleMap
              address={vm.effectiveAddress}
              center={vm.mapCenter}
              zoom={13}
              markers={vm.communityMarkers}
            />
          </div>
        </Modal>

        {/* Offender and Crime Incident Detail Modal */}
        <CommunityRecordDetailModal
          isOpen={Boolean(selectedSafetyRecord)}
          onClose={() => setSelectedSafetyRecord(null)}
          record={selectedSafetyRecord}
          effectiveAddress={vm.effectiveAddress}
        />

        {/* Protest Details Modal */}
        <ProtestDetailsModal
          isOpen={vm.isProtestDetailsOpen}
          onClose={vm.handleProtestDetailsClose}
          selectedPropertyId={vm.selectedPropertyId}
          propertyDetails={vm.propertyDetails}
          user={vm.user}
        />

        {/* Exemption Details Modal */}
        <ExemptionDetailsModal
          isOpen={vm.isExemptionDetailsOpen}
          onClose={vm.handleExemptionDetailsClose}
          selectedPropertyId={vm.selectedPropertyId}
          propertyDetails={vm.propertyDetails}
          user={vm.user}
        />

        {/* Add Property Modal */}
        <Modal
          isOpen={isAddPropertyModalOpen}
          onClose={() => setIsAddPropertyModalOpen(false)}
          ariaLabel="Add New Property"
        >
          <div className={styles.searchModalContent}>
            <div className={styles.modalHeader}>
              <div className={styles.modalIconWrap}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </div>
              <div className={styles.modalHeaderText}>
                <h3 className={styles.modalTitle}>Search & Add Property</h3>
                <p className={styles.modalSubtitle}>
                  Andy will automatically look up your assessed value and tax details
                </p>
              </div>
            </div>

            <AddressSearch
              placeholder="Enter Texas address (e.g. street, city, or zip)"
              buttonText="Add Property"
              preventInternalSubmit={true}
              isSubmitting={isSubmitting}
              searchAddresses={searchAddresses}
              className={styles.modalAddressSearch}
              onAddressSelect={() => {}}
              onButtonClick={async (addressStr, suggestion) => {
                console.log("Add Property Button clicked!", addressStr, suggestion);
                if (suggestion) {
                  await handleAddPropertySubmit({
                    address: suggestion.address,
                    city: suggestion.city,
                    state: suggestion.state,
                    zipCode: suggestion.zipCode,
                    apn: suggestion.apn,
                  });
                } else if (addressStr.trim()) {
                  await handleAddPropertySubmit({
                    address: addressStr,
                  });
                }
                refetchOptions();
              }}
            />
          </div>
        </Modal>

        {/* Enroll To Save More Modal */}
        <Modal
          isOpen={isEnrollModalOpen}
          onClose={() => setIsEnrollModalOpen(false)}
          ariaLabel="Save on More Properties"
          className={styles.enrollModalDialog}
        >
          <div className={styles.enrollModalContent}>
            <div className={styles.modalHeader}>
              <div className={styles.enrollModalIconWrap}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="12 2 2 7 12 12 22 7 12 2" />
                  <polyline points="2 12 12 17 22 12" />
                  <polyline points="2 17 12 22 22 17" />
                </svg>
              </div>
              <div className={styles.modalHeaderText}>
                <div className={styles.enrollModalTitleRow}>
                  <h3 className={styles.modalTitle}>Save on More Properties</h3>
                  <span className={styles.enrollCountBadge}>
                    {eligibleUnpaidProperties.length}{" "}
                    {eligibleUnpaidProperties.length === 1 ? "Property" : "Properties"}
                  </span>
                </div>
                <p className={styles.modalSubtitle}>
                  Properties with unpaid payment status. Select multiple properties to bundle and
                  maximize your savings.
                </p>
              </div>
            </div>

            {eligibleUnpaidProperties.length === 0 ? (
              <div className={styles.enrollEmptyState}>
                <div className={styles.enrollEmptyIcon}>✓</div>
                <h4 className={styles.enrollEmptyTitle}>All Properties Enrolled</h4>
                <p className={styles.enrollEmptyDesc}>
                  All of your registered properties have already completed protest payment.
                </p>
              </div>
            ) : (
              <>
                <div className={styles.enrollSubheader}>
                  <label className={styles.enrollSelectAllLabel}>
                    <input
                      type="checkbox"
                      checked={isAllEnrollSelected}
                      onChange={handleToggleSelectAllEnroll}
                      className={styles.enrollHiddenCheckbox}
                    />
                    <span
                      className={`${styles.enrollCheckbox} ${
                        isAllEnrollSelected ? styles.enrollCheckboxChecked : ""
                      }`}
                    >
                      {isAllEnrollSelected && (
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </span>
                    <span className={styles.enrollSelectAllText}>
                      Select All ({eligibleUnpaidProperties.length})
                    </span>
                  </label>
                  <div className={styles.enrollSubheaderRight}>
                    <div className={styles.enrollSelectedCounter}>
                      <span className={styles.enrollSelectedCountNumber}>
                        {selectedEnrollPropertyIds.length}
                      </span>{" "}
                      of {eligibleUnpaidProperties.length} selected
                    </div>
                    {totalSelectedSavings > 0 && (
                      <div className={styles.enrollSubheaderSavingsPill}>
                        Total Est. Savings:{" "}
                        <strong>{vm.formatCurrency(totalSelectedSavings)}</strong>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.enrollPropertyList}>
                  {eligibleUnpaidProperties.map((prop) => {
                    const { line1, line2 } = parsePropertyAddress(
                      prop.address?.full_address || prop.name || prop.property_id
                    );
                    const isCurrent = selectedAddress === prop.property_id;
                    const isChecked = selectedEnrollPropertyIds.includes(prop.property_id);
                    const streetImg = streetViewImages[prop.property_id];
                    const statusInfo = getProtestStatusInfo(prop.protest_status);
                    const estSavings = calculatePropertySavings(prop);
                    const marketValueDisplay = prop.tax_info?.market_value ?? prop.estimated_value;

                    return (
                      <div
                        key={prop.property_id}
                        className={`${styles.enrollPropertyCard} ${
                          isChecked ? styles.enrollPropertyCardSelected : ""
                        } ${isCurrent ? styles.enrollPropertyCardActive : ""}`}
                        onClick={() => handleToggleEnrollProperty(prop.property_id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleToggleEnrollProperty(prop.property_id);
                          }
                        }}
                      >
                        <div
                          className={styles.enrollCheckboxWrapper}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <label className={styles.enrollCardCheckboxLabel}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggleEnrollProperty(prop.property_id)}
                              className={styles.enrollHiddenCheckbox}
                            />
                            <span
                              className={`${styles.enrollCheckbox} ${
                                isChecked ? styles.enrollCheckboxChecked : ""
                              }`}
                            >
                              {isChecked && (
                                <svg
                                  width="11"
                                  height="11"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </span>
                          </label>
                        </div>

                        <div className={styles.enrollCardImgWrap}>
                          {streetImg ? (
                            <img src={streetImg} alt={line1} className={styles.enrollCardImg} />
                          ) : (
                            <div className={styles.enrollCardImgPlaceholder}>
                              <img
                                src={Images.noPropertyHome}
                                alt="Property"
                                className={styles.enrollCardFallbackIcon}
                              />
                            </div>
                          )}
                          <span className={styles.enrollUnpaidBadge}>Not File</span>
                        </div>

                        <div className={styles.enrollCardDetails}>
                          <div className={styles.enrollCardHeader}>
                            <h4 className={styles.enrollCardAddress} title={line1}>
                              {line1}
                            </h4>
                            <p className={styles.enrollCardLocation} title={line2}>
                              {line2}
                            </p>
                          </div>

                          <div className={styles.enrollCardMetaRow}>
                            <div className={styles.enrollMetaItem}>
                              <span className={styles.enrollMetaLabel}>Protest:</span>
                              <span
                                className={`${styles.statusMetaValue} ${statusInfo.badgeClass}`}
                              >
                                {statusInfo.badgeLabel}
                              </span>
                            </div>
                            <div className={styles.enrollMetaItem}>
                              <span className={styles.enrollMetaLabel}>Payment:</span>
                              <span
                                className={
                                  prop.payment_status === "paid"
                                    ? styles.enrollPaymentPaidPill
                                    : styles.enrollPaymentUnpaidPill
                                }
                              >
                                {prop.payment_status === "paid" ? "Paid" : "Unpaid"}
                              </span>
                            </div>
                            <div className={styles.enrollMetaItem}>
                              <span className={styles.enrollMetaLabel}>Market Value:</span>
                              <span className={styles.enrollMetaValue}>
                                {vm.formatCurrency(marketValueDisplay)}
                              </span>
                            </div>
                            <div className={styles.enrollMetaItem}>
                              <span className={styles.enrollMetaLabel}>Est. Savings:</span>
                              <span className={styles.enrollSavingsValue}>
                                {estSavings > 0 ? vm.formatCurrency(estSavings) : "—"}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div
                          className={styles.enrollCardActions}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {prop.payment_status === null && (
                            <button
                              type="button"
                              className={styles.enrollDeleteBtn}
                              onClick={() => void handleDeleteProperty(prop.property_id)}
                              disabled={deletingPropertyId === prop.property_id}
                              aria-label={`Delete ${line1 || "property"}`}
                              title="Delete Property"
                            >
                              {deletingPropertyId === prop.property_id ? (
                                <span className={styles.deletingSpinner} />
                              ) : (
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            className={styles.enrollPayBtn}
                            onClick={() => {
                              setSelectedAddress(prop.property_id);
                              setIsEnrollModalOpen(false);
                              navigate(routes.protestPayment, {
                                state: {
                                  selectedProperty: prop.property_id,
                                  propertyDetails: prop,
                                  selectedProperties: [
                                    { property_id: prop.property_id, selected: true },
                                  ],
                                },
                              });
                            }}
                          >
                            Pay & File ➔
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className={styles.enrollModalFooter}>
                  <div className={styles.enrollFooterSummary}>
                    <div className={styles.enrollFooterSelectedText}>
                      <strong>{selectedEnrollPropertyIds.length}</strong>{" "}
                      {selectedEnrollPropertyIds.length === 1 ? "property" : "properties"} selected
                    </div>
                    <div className={styles.enrollFooterSavingsRow}>
                      <span className={styles.enrollFooterSavingsLabel}>
                        Total Estimated Savings:
                      </span>
                      <span className={styles.enrollFooterSavingsAmount}>
                        {totalSelectedSavings > 0 ? vm.formatCurrency(totalSelectedSavings) : "$0"}
                      </span>
                    </div>
                    <div className={styles.enrollFooterSubtext}>
                      Bundle multiple properties to save on protest filing
                    </div>
                  </div>

                  <div className={styles.enrollFooterActions}>
                    <button
                      type="button"
                      className={styles.enrollCancelBtn}
                      onClick={() => setIsEnrollModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={selectedEnrollPropertyIds.length === 0}
                      className={styles.enrollBulkPayBtn}
                      onClick={handleBulkPayAndFile}
                    >
                      Pay & File for {selectedEnrollPropertyIds.length}{" "}
                      {selectedEnrollPropertyIds.length === 1 ? "Property" : "Properties"} ➔
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal>

        {/* Ready to File Protest Modal */}
        <Modal
          isOpen={isFileModalOpen}
          onClose={() => setIsFileModalOpen(false)}
          ariaLabel="Ready to File Your Protest"
          className={styles.enrollModalDialog}
        >
          <div className={styles.enrollModalContent}>
            <div className={styles.modalHeader}>
              <div className={styles.enrollModalIconWrap}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                  <path d="M12 11v4" />
                  <path d="M12 18h.01" />
                </svg>
              </div>
              <div className={styles.modalHeaderText}>
                <div className={styles.enrollModalTitleRow}>
                  <h3 className={styles.modalTitle}>Ready to File Your Protest</h3>
                  <span className={styles.enrollCountBadge}>
                    {eligiblePaidProperties.length}{" "}
                    {eligiblePaidProperties.length === 1 ? "Property" : "Properties"}
                  </span>
                </div>
                <p className={styles.modalSubtitle}>
                  Properties with paid payment status. Select properties to sign documents and file
                  your protest.
                </p>
              </div>
            </div>

            {eligiblePaidProperties.length === 0 ? (
              <div className={styles.enrollEmptyState}>
                <div className={styles.enrollEmptyIcon}>✓</div>
                <h4 className={styles.enrollEmptyTitle}>No Properties Ready to File</h4>
                <p className={styles.enrollEmptyDesc}>
                  You don&apos;t have any properties with completed payment awaiting document
                  signature.
                </p>
              </div>
            ) : (
              <>
                <div className={styles.enrollSubheader}>
                  <label className={styles.enrollSelectAllLabel}>
                    <input
                      type="checkbox"
                      checked={isAllFileSelected}
                      onChange={handleToggleSelectAllFile}
                      className={styles.enrollHiddenCheckbox}
                    />
                    <span
                      className={`${styles.enrollCheckbox} ${
                        isAllFileSelected ? styles.enrollCheckboxChecked : ""
                      }`}
                    >
                      {isAllFileSelected && (
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </span>
                    <span className={styles.enrollSelectAllText}>
                      Select All ({eligiblePaidProperties.length})
                    </span>
                  </label>
                  <div className={styles.enrollSubheaderRight}>
                    <div className={styles.enrollSelectedCounter}>
                      <span className={styles.enrollSelectedCountNumber}>
                        {selectedFilePropertyIds.length}
                      </span>{" "}
                      of {eligiblePaidProperties.length} selected
                    </div>
                    {totalFileSelectedSavings > 0 && (
                      <div className={styles.enrollSubheaderSavingsPill}>
                        Total Est. Savings:{" "}
                        <strong>{vm.formatCurrency(totalFileSelectedSavings)}</strong>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.enrollPropertyList}>
                  {eligiblePaidProperties.map((prop) => {
                    const { line1, line2 } = parsePropertyAddress(
                      prop.address?.full_address || prop.name || prop.property_id
                    );
                    const isCurrent = selectedAddress === prop.property_id;
                    const isChecked = selectedFilePropertyIds.includes(prop.property_id);
                    const streetImg = streetViewImages[prop.property_id];
                    const statusInfo = getProtestStatusInfo(prop.protest_status);
                    const estSavings = calculatePropertySavings(prop);
                    const marketValueDisplay = prop.tax_info?.market_value ?? prop.estimated_value;

                    return (
                      <div
                        key={prop.property_id}
                        className={`${styles.enrollPropertyCard} ${
                          isChecked ? styles.enrollPropertyCardSelected : ""
                        } ${isCurrent ? styles.enrollPropertyCardActive : ""}`}
                        onClick={() => handleToggleFileProperty(prop.property_id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleToggleFileProperty(prop.property_id);
                          }
                        }}
                      >
                        <div
                          className={styles.enrollCheckboxWrapper}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <label className={styles.enrollCardCheckboxLabel}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggleFileProperty(prop.property_id)}
                              className={styles.enrollHiddenCheckbox}
                            />
                            <span
                              className={`${styles.enrollCheckbox} ${
                                isChecked ? styles.enrollCheckboxChecked : ""
                              }`}
                            >
                              {isChecked && (
                                <svg
                                  width="11"
                                  height="11"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </span>
                          </label>
                        </div>

                        <div className={styles.enrollCardImgWrap}>
                          {streetImg ? (
                            <img src={streetImg} alt={line1} className={styles.enrollCardImg} />
                          ) : (
                            <div className={styles.enrollCardImgPlaceholder}>
                              <img
                                src={Images.noPropertyHome}
                                alt="Property"
                                className={styles.enrollCardFallbackIcon}
                              />
                            </div>
                          )}
                          <span className={styles.enrollPaidBadge}>Paid</span>
                        </div>

                        <div className={styles.enrollCardDetails}>
                          <div className={styles.enrollCardHeader}>
                            <h4 className={styles.enrollCardAddress} title={line1}>
                              {line1}
                            </h4>
                            <p className={styles.enrollCardLocation} title={line2}>
                              {line2}
                            </p>
                          </div>

                          <div className={styles.enrollCardMetaRow}>
                            <div className={styles.enrollMetaItem}>
                              <span className={styles.enrollMetaLabel}>Protest:</span>
                              <span
                                className={`${styles.statusMetaValue} ${statusInfo.badgeClass}`}
                              >
                                {statusInfo.badgeLabel}
                              </span>
                            </div>
                            <div className={styles.enrollMetaItem}>
                              <span className={styles.enrollMetaLabel}>Payment:</span>
                              <span className={styles.enrollPaymentPaidPill}>Paid</span>
                            </div>
                            <div className={styles.enrollMetaItem}>
                              <span className={styles.enrollMetaLabel}>Market Value:</span>
                              <span className={styles.enrollMetaValue}>
                                {vm.formatCurrency(marketValueDisplay)}
                              </span>
                            </div>
                            <div className={styles.enrollMetaItem}>
                              <span className={styles.enrollMetaLabel}>Est. Savings:</span>
                              <span className={styles.enrollSavingsValue}>
                                {estSavings > 0 ? vm.formatCurrency(estSavings) : "—"}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div
                          className={styles.enrollCardActions}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className={styles.enrollPayBtn}
                            onClick={() => {
                              setSelectedAddress(prop.property_id);
                              setIsFileModalOpen(false);
                              navigate(routes.selectPropertyType, {
                                state: {
                                  paidPropertyIds: [prop.property_id],
                                },
                              });
                            }}
                          >
                            Sign & File ➔
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className={styles.enrollModalFooter}>
                  <div className={styles.enrollFooterSummary}>
                    <div className={styles.enrollFooterSelectedText}>
                      <strong>{selectedFilePropertyIds.length}</strong>{" "}
                      {selectedFilePropertyIds.length === 1 ? "property" : "properties"} selected
                    </div>
                    <div className={styles.enrollFooterSavingsRow}>
                      <span className={styles.enrollFooterSavingsLabel}>
                        Total Estimated Savings:
                      </span>
                      <span className={styles.enrollFooterSavingsAmount}>
                        {totalFileSelectedSavings > 0
                          ? vm.formatCurrency(totalFileSelectedSavings)
                          : "$0"}
                      </span>
                    </div>
                    <div className={styles.enrollFooterSubtext}>
                      Sign documents to file protest for selected properties
                    </div>
                  </div>

                  <div className={styles.enrollFooterActions}>
                    <button
                      type="button"
                      className={styles.enrollCancelBtn}
                      onClick={() => setIsFileModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={selectedFilePropertyIds.length === 0}
                      className={styles.enrollBulkPayBtn}
                      onClick={handleBulkSignAndFile}
                    >
                      Sign & File for {selectedFilePropertyIds.length}{" "}
                      {selectedFilePropertyIds.length === 1 ? "Property" : "Properties"} ➔
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal>

        {/* Settlement Waiver Document Viewer Modal */}
        <Modal
          isOpen={vm.isSettlementWaiverViewerOpen}
          onClose={vm.closeSettlementWaiverViewer}
          ariaLabel="Settlement Waiver Document"
          unconstrained
          className={styles.settlementModalContent}
          headerClassName={styles.settlementModalHeader}
          title={
            <div className={styles.settlementModalTitleRow}>
              <span>Settlement Waiver Document</span>
              <div className={styles.settlementHeaderActions}>
                <button
                  type="button"
                  className={styles.settlementDownloadBtn}
                  onClick={vm.handleDownloadSettlementWaiverDocument}
                  disabled={vm.isSettlementWaiverDownloading}
                  title="Download PDF"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  <span>{vm.isSettlementWaiverDownloading ? "Downloading..." : "Download"}</span>
                </button>
                {vm.settlementWaiverDocumentUrl && (
                  <a
                    href={vm.settlementWaiverDocumentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.settlementExternalLink}
                    title="Open in new tab"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                    <span>Open in New Tab</span>
                  </a>
                )}
              </div>
            </div>
          }
          bodyClassName={styles.settlementModalBody}
        >
          <div className={styles.settlementDocViewer}>
            {vm.settlementWaiverDocumentUrl ? (
              <iframe
                src={`${vm.settlementWaiverDocumentUrl}#toolbar=1&navpanes=1`}
                title="Settlement Waiver Document Preview"
                className={styles.settlementIframe}
              />
            ) : (
              <div className={styles.settlementEmptyState}>
                <p>No document available to display.</p>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};
